import React, { useState, useEffect } from "react";
import FormFieldset from "../../../../components/FormFieldset/FormFieldset";
import Button from "../../../../components/Button/Button";
import FormDropDown from "../../../../components/FormDropDown/FormDropDown";
import { useForm } from "react-hook-form";
import FormInput from "../../../../components/FormInput/FormInput";
import { postcodeValidator } from "postcode-validator";
import { toast } from "react-toastify";
import styled from "styled-components";
import { keyBy } from "lodash";
import { green } from "../../../../settings";
import ServiceService from "../../../../services/ServiceService/ServiceService";
import { addFormattedAddress } from "../../../../utils/functions/serviceFunctions";
import AppLoading from "../../../../AppLoading";

const StyledHighlightedText = styled.p`
  font-size: 19px;
  font-weight: bold;
  margin: 0;
`;
const StyledHighlightedInfoText = styled.p`
  font-size: 12px;
  margin: 20px;
`;

const StyledPostcodeText = styled.p`
  font-size: 16px;
  font-weight: bold;
  margin: 5px 10px 5px 0;
`;

const StyledClickText = styled.a`
  display: block;
  cursor: pointer;
  font-size: 16px;
  margin: 5px 10px 0 0;
`;

const StyledHr = styled.hr`
  margin: 40px 0;
`;

const ServiceLocationsForm = ({
  index,
  defaultValues = {},
  setSelectedAddressArray,
  selectedAddressArray,
  addressCounter,
  setAddressCounter,
  setErrorMessage,
  organisationRemoteType,
}) => {
  const [selectedPostcodeValue, setSelectedPostcodeValue] = useState(
    defaultValues.postal_code || "",
  );
  const [postcodeHasBeenRemoved, setPostcodeHasBeenRemoved] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [addressesIsLoading, setAddressesIsLoading] = useState(false);
  const [isRemoteType, setIsRemoteType] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    reset,
    watch,
  } = useForm({
    mode: "onSubmit",
  });

  useEffect(() => {
    if (!defaultValues.postal_code) return;
    doFindAddress(defaultValues.postal_code);
  }, [defaultValues.postal_code]);

  async function doFindAddress(postcode) {
    if (addressesIsLoading) return;

    setAddressesIsLoading(true);

    const data = await ServiceService.findAddress(postcode);

    setAddressesIsLoading(false);

    if (data) {
      setSelectedPostcodeValue(postcode);

      const dataWithFormattedAddress = addFormattedAddress(data);

      setAddresses(keyBy(dataWithFormattedAddress, "formattedAddress"));

      reset({
        address: defaultValues.formattedAddress,
      });
      if (organisationRemoteType) {
        const defaultAddresses = [...selectedAddressArray];
        defaultAddresses[0] = addresses[getValues().address];
        setIsRemoteType(true);
      }
    } else {
      setSelectedPostcodeValue("");
      toast.error(`Postcode ${postcode} could not be found.`);
    }
  }

  function doChangePostcode() {
    const updatedSelectedAddressArray = [...selectedAddressArray];
    updatedSelectedAddressArray[index] = {};

    setSelectedAddressArray(updatedSelectedAddressArray);

    setSelectedPostcodeValue("");
  }

  function doRemovePostcode() {
    const updatedSelectedAddressArray = [...selectedAddressArray];
    updatedSelectedAddressArray[index] = {};

    setSelectedAddressArray(updatedSelectedAddressArray);

    setSelectedPostcodeValue("");
    setPostcodeHasBeenRemoved(true);
  }

  function onDropDownChange() {
    const updatedSelectedAddressArray = [...selectedAddressArray];
    updatedSelectedAddressArray[index] = addresses[getValues().address];

    setSelectedAddressArray(updatedSelectedAddressArray);
  }

  function doAddAnotherLocation() {
    setAddressCounter((addressCounter) => addressCounter + 1);
  }

  function renderAddAnotherLocationButton({ hideForRemote = true } = {}) {
    if (index + 1 !== addressCounter) return null;
    if (hideForRemote && isRemoteType) return null;

    return (
      <Button
        label="Add another location"
        margin="60px 0 10px 0"
        backgroundColor="white"
        color={green[400]}
        border={`1px solid ${green[400]}`}
        padding="13px 9px"
      />
    );
  }

  function renderRemovedPostcodeForm() {
    return (
      <form onSubmit={handleSubmit(doAddAnotherLocation)}>
        {renderAddAnotherLocationButton({ hideForRemote: false })}
      </form>
    );
  }

  function renderPostcodeSearchForm() {
    return (
      <form
        onSubmit={handleSubmit(() => {
          const postcode = getValues().postcode.toUpperCase();

          doFindAddress(postcode);

          return true;
        })}
        style={{ margin: index > 0 ? "40px 0 60px 0" : "0 0 60px 0" }}
      >
        {index > 0 ? <StyledHr /> : null}
        <FormInput
          name="postcode"
          type="text"
          label="Postcode"
          onChange={() => setErrorMessage("")}
          register={register}
          labelStyle={{ fontWeight: "bold" }}
          validate={{
            pattern: (value) => {
              return postcodeValidator(value, "UK") || "Please enter a valid postcode";
            },
          }}
          error={errors.postcode}
          required
        />
        <Button type="submit" label="Find address" padding="13px 47px" />
      </form>
    );
  }

  function renderSelectedAddressForm() {
    const selectedAddress = addresses[watch("address")];

    return (
      <form onSubmit={handleSubmit(doAddAnotherLocation)}>
        {index > 0 ? <StyledHr /> : null}
        <StyledHighlightedText>Postcode</StyledHighlightedText>
        <div style={{ display: "flex", marginBottom: "20px" }}>
          <StyledPostcodeText>{selectedPostcodeValue}</StyledPostcodeText>
          {!isRemoteType && (
            <StyledClickText onClick={doChangePostcode}>Change</StyledClickText>
          )}
          {index > 0 ? (
            <StyledClickText onClick={doRemovePostcode}>Remove</StyledClickText>
          ) : null}
        </div>
        <FormDropDown
          label={"Select an address"}
          name={"address"}
          register={register}
          required
          onChange={onDropDownChange}
          options={Object.keys(addresses).map((key) => {
            return addresses[key].formattedAddress;
          })}
          error={errors.address}
        />
        {isRemoteType && (
          <StyledHighlightedInfoText>
            Please note, if you have told us you are a remote organisation, your location
            on the map is automatically set to the Hackney Town Hall. If you would like to
            change this, please edit your organisation details, or contact{" "}
            <a href="mailto:fss@hackney.gov.uk">fss@hackney.gov.uk</a>.
          </StyledHighlightedInfoText>
        )}
        If you have can't find your address in the list please contact{" "}
        <a href="mailto:fss@hackney.gov.uk">fss@hackney.gov.uk</a>
        {selectedAddress ? <p>UPRN: {selectedAddress.uprn}</p> : null}
        {renderAddAnotherLocationButton()}
      </form>
    );
  }

  if (addressesIsLoading) {
    return (
      <div style={{ height: "270px" }}>
        <AppLoading />
      </div>
    );
  }

  if (postcodeHasBeenRemoved) return renderRemovedPostcodeForm();

  return selectedPostcodeValue ? renderSelectedAddressForm() : renderPostcodeSearchForm();
};

export default ServiceLocationsForm;
